/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'
import {useIntl} from 'react-intl'
import useTagManager from '../../hooks/use-tag-manager'

// Components
import {
    Breadcrumb as ChakraBreadcrumb,
    BreadcrumbItem as ChakraBreadcrumbItem,
    BreadcrumbLink as ChakraBreadcrumbLink,
    Skeleton,
    // Hooks
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
const DEFAULT_SKELETON_COUNT = 2
// Others
import {categoryUrlBuilder} from '../../utils/url'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/**
 * A simplification of the Chakra `Breadcrumb` component for our project needs. Given
 * a list of categories, display a breadcrumb and it's items.
 */
const Breadcrumb = ({categories, ...rest}) => {
    const intl = useIntl()
    const styles = useStyleConfig('Breadcrumb')
    const {
        app: {commerceAPI}
    } = getConfig()
    const {rootCategoryId} = commerceAPI.storefront || {}
    const {ispdp, isProductLoading} = rest
    const tagManager = useTagManager()
    const filteredCategories = categories?.filter(
        (category) => category.id !== rootCategoryId && !category.c_hideInBreadcrumb
    )
    return (
        <ChakraBreadcrumb className="sf-breadcrumb" {...styles.container} separator="/">
            {filteredCategories?.map((category) => {
                const breadcrumbLink = {
                    as: RouteLink,
                    to: categoryUrlBuilder(category, intl.locale)
                }
                if (category.htmlValue) {
                    category.name = category.htmlValue
                }
                return (
                    <ChakraBreadcrumbItem
                        key={category.id}
                        data-testid="sf-crumb-item"
                        onClick={() => {
                            tagManager.navigationMain(
                                'breadcrumb',
                                filteredCategories?.[0]?.name,
                                filteredCategories?.[2]?.name
                                    ? filteredCategories?.[2]?.name
                                    : filteredCategories?.[1]?.name,
                                filteredCategories?.[2]?.name
                                    ? filteredCategories?.[1]?.name
                                    : filteredCategories?.[2]?.name,
                                'breadcrumb-link'
                            )
                        }}
                    >
                        <ChakraBreadcrumbLink {...breadcrumbLink} sx={styles.link}>
                            {category.name}
                        </ChakraBreadcrumbLink>
                    </ChakraBreadcrumbItem>
                )
            })}
            {!categories ||
                (ispdp &&
                    isProductLoading &&
                    new Array(DEFAULT_SKELETON_COUNT).fill().map((_, index) => {
                        return (
                            <ChakraBreadcrumbItem key={index}>
                                <ChakraBreadcrumbLink>
                                    <Skeleton width="50px" height="16px" />
                                </ChakraBreadcrumbLink>
                            </ChakraBreadcrumbItem>
                        )
                    }))}
        </ChakraBreadcrumb>
    )
}

Breadcrumb.displayName = 'Breadcrumb'

Breadcrumb.propTypes = {
    /**
     * The categories to be displayed in this breadcrumb.
     */
    categories: PropTypes.array
}

export default Breadcrumb
